import { Dispatch, SetStateAction, useState, useCallback, useMemo } from 'react';
import { ViewStatus } from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import { VoicemailTypes as VmTypes, VoicemailTypes } from '@frontend/api-voicemails';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Actions, ActionsUI } from '@frontend/contact-actions';
import { i18next, useTranslation } from '@frontend/i18n';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { useModalControl } from '@frontend/design-system';
import { VoicemailTableDataRow } from '../../../components/all-calls/types';
import { useDeleteVoicemailConfirmation } from '../../../components/voicemails/use-delete-voicemail-confirmation';
import { downloadVoicemail } from '../../../components/voicemails/voicemail-utils';
import { Forwardvoicemail } from '../../../components/voicemails/voicemails-forward';
import {
  defaultForwardVoicemailModalData,
  ForwardVoicemailDynamicData,
  ForwardVoicemailModalData,
} from '../../../components/voicemails/voicemails-multi.component';
import { useVoicemailBoxList } from '../../../hooks/use-voicemail-box';
import { getShortHandName } from '../../utils';

export const SplitViewActions = ({
  data,
  index,
  onDelete,
  setIsTagEditing,
  onForwardSuccess,
  updateReadStatus,
  trackingId,
}: {
  data: VoicemailTableDataRow;
  index: number;
  onDelete: (payload: VmTypes.DeletePayload) => void;
  setIsTagEditing: Dispatch<SetStateAction<boolean>>;
  onForwardSuccess: () => void;
  updateReadStatus: (payload: VoicemailTypes.StatusPayload) => void;
  trackingId?: string;
}) => {
  const { t } = useTranslation('calls', { keyPrefix: 'unread-voicemail-widget' });
  const { selectedLocationIds } = useAppScopeStore();
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');

  const { data: voicemailBoxList } = useVoicemailBoxList({
    phoneConfig,
    locationIds: selectedLocationIds,
  });

  const { onClick: messageOnClick } = ActionsUI.actions.useMessageAction();

  const [forwardVoicemailModalData, setForwardVoicemailModalData] = useState<ForwardVoicemailModalData>(
    defaultForwardVoicemailModalData as ForwardVoicemailModalData
  );
  const doubleActionControl = useModalControl();
  const { openDeleteModal, confirmationModal: deleteVoicemailModal } = useDeleteVoicemailConfirmation<{
    row: VoicemailTableDataRow;
    index: number;
  }>({
    title: i18next.t('Delete Voicemail Message?'),
    message: i18next.t('Are you sure you want to delete this voicemail?'),
    confirmLabel: i18next.t('Yes, Delete'),
    cancelLabel: i18next.t('No, Cancel'),
    onConfirm: ({ row, index }) => {
      onDelete({
        voicemailId: row.forwardedMessageId || row.channelId,
        index,
        mailboxId: row.mailboxId,
      });
    },
  });

  const openForwardVoicemailModal = useCallback(
    (fwdVmData: ForwardVoicemailDynamicData) => {
      setForwardVoicemailModalData({ ...fwdVmData, voicemailBoxList: voicemailBoxList || [], onDelete: onDelete });
      doubleActionControl.openModal();
    },
    [voicemailBoxList, onDelete, doubleActionControl]
  );

  const handleForwardVoicemail = useCallback(() => {
    openForwardVoicemailModal({
      vmId: data.voicemailId == '' ? data.channelId : data.voicemailId,
      mediaId: data.mediaId,
      mediaPath: data.mediaFilePath,
      currentVoicemailBoxId: data.mailboxId,
      currentVoicemailBoxName: data.voiceMailBoxName,
    } as ForwardVoicemailDynamicData);
  }, [openForwardVoicemailModal, data]);

  const handleAddTag = useCallback(() => {
    setIsTagEditing(true);
  }, [setIsTagEditing]);

  const handleDownloadVoicemail = useCallback(() => {
    downloadVoicemail(data.mediaFilePath, data.contactName ?? 'unknown', data.dateTime);
  }, [data.mediaFilePath, data.contactName, data.dateTime]);

  const handleMarkUnread = useCallback(() => {
    if (!!data) {
      updateReadStatus({
        index: index,
        voicemailId: data?.forwardedMessageId || data.channelId,
        status: ViewStatus.VIEW_STATUS_UNREAD,
      });
    }
  }, [data, index, updateReadStatus]);

  const handleMarkRead = useCallback(() => {
    if (!!data) {
      updateReadStatus({
        index: index,
        voicemailId: data?.forwardedMessageId || data.channelId,
        status: ViewStatus.VIEW_STATUS_READ,
      });
    }
  }, [data, index, updateReadStatus]);

  const handleDeleteVoicemail = useCallback(() => {
    if (!data) return;
    openDeleteModal({ row: data, index });
  }, [data, index, openDeleteModal]);

  const callContext = useMemo(
    () => ({
      personId: data.contactId,
      phoneNumber: data.contactNumber,
      locationId: selectedLocationIds[0] ?? '',
    }),
    [data.contactId, data.contactNumber, selectedLocationIds]
  );

  const messageContext = useMemo(
    () => ({
      personId: data.contactId,
      personPhone: data.contactNumber,
      groupIds: data.locationIds,
    }),
    [data.contactId, data.contactNumber, data.locationIds]
  );

  return (
    <>
      <Forwardvoicemail
        onForwardSuccess={onForwardSuccess}
        modalProps={doubleActionControl.modalProps}
        forwardData={forwardVoicemailModalData}
      />
      {deleteVoicemailModal}

      <AdaptoActions maxActions={3} variant='icon-buttons' iconColor='default'>
        <Actions.Call
          label={`${t('Call')} ${getShortHandName(data)}`}
          context={callContext}
          trackingIdSuffix={`${trackingId}-phone-icn-btn`}
        />
        <Actions.Message
          label={`${t('Message')} ${getShortHandName(data)}`}
          context={messageContext}
          iconName='message'
          trackingIdSuffix={`${trackingId}-message-icn-btn`}
          onClick={() => {
            messageOnClick(messageContext);
          }}
        />
        <Actions.View
          key='view-profile-action'
          personId={data.contactId}
          trackingId={`${trackingId}-btn-tablerowaction-viewprofile`}
          label={t('View Profile')}
        />
        <AdaptoActions.Action
          icon='voicemail-override'
          label={t('Forward Voicemail')}
          trackingId={`${trackingId}-btn-tablerowaction-forwardvoicemail`}
          onClick={handleForwardVoicemail}
          id='forward-voicemail-action'
        />
        <AdaptoActions.Action
          icon='label'
          label={t('Add Tag')}
          trackingId={`${trackingId}-btn-tablerowaction-tagvoicemail`}
          onClick={handleAddTag}
          id='add-tag-action'
        />
        <AdaptoActions.Action
          icon='download'
          label={t('Download Voicemail')}
          trackingId={`${trackingId}-btn-tablerowaction-downloadvoicemail`}
          onClick={handleDownloadVoicemail}
          id='download-voicemail-action'
        />
        {data.readAt ? (
          <AdaptoActions.Action
            icon='mark-unread'
            label={t('Mark Unread')}
            trackingId={`${trackingId}-btn-tablerowaction-togglestatus`}
            onClick={handleMarkUnread}
            id='mark-unread-action'
          />
        ) : (
          <AdaptoActions.Action
            icon='mark-read'
            label={t('Mark Read')}
            trackingId={`${trackingId}-btn-tablerowaction-togglestatus`}
            onClick={handleMarkRead}
            id='mark-read-action'
          />
        )}
        <AdaptoActions.Action
          icon='trash'
          label={t('Delete Voicemail')}
          trackingId={`${trackingId}-btn-tablerowaction-deletevoicemail`}
          destructive={true}
          onClick={handleDeleteVoicemail}
          id='delete-voicemail-action'
        />
      </AdaptoActions>
    </>
  );
};
